import React from "react"
import {Truck, Battery, Settings, CheckCircle} from 'lucide-react'

const OnanPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-700 to-green-600 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Onan RV Generator Auto Start Systems
            </h1>
            <p className="text-xl text-green-50 leading-relaxed mb-4">
              Remote start and automatic control solutions for Cummins Onan generators in RVs, motorhomes, and commercial applications.
            </p>
            <p className="text-lg text-green-100">
              Industry standard for RV power, Onan generators offer excellent integration with remote start and monitoring systems.
            </p>
          </div>
        </div>
      </section>

      {/* Compatible Series */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Compatible Onan Generator Series
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-600">
              <h3 className="text-lg font-bold text-gray-900 mb-3">QG Series</h3>
              <p className="text-sm text-gray-600 mb-2">Gasoline RV Generators</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• QG 2500 / 2800</li>
                <li>• QG 4000 / 5500</li>
                <li>• QG 7000</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-600">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Marquis Gold</h3>
              <p className="text-sm text-gray-600 mb-2">Premium Quiet Series</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• 5500 / 6500</li>
                <li>• 7000 / 8000</li>
                <li>• Built-in remote ready</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-600">
              <h3 className="text-lg font-bold text-gray-900 mb-3">MicroQuiet</h3>
              <p className="text-sm text-gray-600 mb-2">Ultra-Quiet Inverter</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• 2000i / 3600iE</li>
                <li>• 4000 MicroQuiet</li>
                <li>• Factory remote option</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-600">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Commercial</h3>
              <p className="text-sm text-gray-600 mb-2">Heavy-Duty Models</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• RV QD 3200 / 6500</li>
                <li>• HDKAJ / HDKAH</li>
                <li>• Auto-start capable</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Onan Generator Automation Benefits
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <Truck size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">RV Integration</h3>
              <p className="text-gray-600">
                Designed specifically for motorhomes and travel trailers
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <Battery size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Battery Charging</h3>
              <p className="text-gray-600">
                Automatic start when house batteries drop below threshold
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <Settings size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Climate Control</h3>
              <p className="text-gray-600">
                Power AC/heating automatically for pet safety
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <CheckCircle size={32} className="text-green-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">OEM Compatible</h3>
              <p className="text-gray-600">
                Works with factory remote start prep and panels
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Remote Start Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Remote Start Solutions for Onan Generators
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <div className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                FACTORY OPTION
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Onan Remote Start Panel
              </h3>
              <p className="text-gray-700 mb-4">
                Factory-installed dashboard switch panel for convenient generator control from inside the RV.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Start/stop controls</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Run time display</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Fault indicators</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Pre-wired from factory</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                AFTERMARKET
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Wireless Key Fob Remote
              </h3>
              <p className="text-gray-700 mb-4">
                Add wireless remote capability to generators without factory remote start prep.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>300-500ft range</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Multiple fobs supported</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>2-3 hour installation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Works with all QG series</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 text-blue-600 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                SMART AUTOMATION
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Automatic Start Controller
              </h3>
              <p className="text-gray-700 mb-4">
                Intelligent auto-start based on battery voltage, temperature, or time schedules.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Voltage sensing trigger</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Temperature-based start</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Scheduled exercise mode</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5" />
                  <span>Smartphone app control</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Wiring Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Onan Generator Control Wiring
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-md mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Standard Remote Start Connections
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">6-Pin Remote Connector</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li><strong>Pin 1:</strong> Start (+12V)</li>
                  <li><strong>Pin 2:</strong> Stop (Ground)</li>
                  <li><strong>Pin 3:</strong> Prime (if equipped)</li>
                  <li><strong>Pin 4:</strong> Ground</li>
                  <li><strong>Pin 5:</strong> Run sense (+12V)</li>
                  <li><strong>Pin 6:</strong> Fault (+12V)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Common Wire Colors</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li><strong>Yellow:</strong> Start signal</li>
                  <li><strong>Gray:</strong> Stop signal</li>
                  <li><strong>Brown:</strong> Prime (diesel models)</li>
                  <li><strong>Black:</strong> Ground/Common</li>
                  <li><strong>Green:</strong> Run indicator</li>
                  <li><strong>Red:</strong> Fault/alarm</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded">
            <p className="text-gray-700">
              <strong>Warning:</strong> Always refer to your specific Onan model service manual for exact wiring diagrams. Incorrect wiring can damage the control board or void warranty.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Upgrade Your Onan RV Generator
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Find installation guides, wiring diagrams, and product recommendations for your Onan generator model.
          </p>
        </div>
      </section>
    </div>
  )
}

export default OnanPage